import { useAuth } from "@elit-hair/elit-tools";
import { StatusCodes } from "http-status-codes";
import { useEffect, useRef, useState } from "react";
import { Socket, io } from "socket.io-client";

import { ENV } from "@/constants/env";
import { getCurrentAuthToken, handleTokenRefreshFlow } from "@/utils/auth-token.utils";

// Type for socket events - only string keys allowed
type SocketEvents = Record<string, unknown>;
type InternalSocketEvents = {
    http_error: (payload?: { status?: number } | null) => void;
};

const SOCKET_INTERNAL_EVENTS = {
    HTTP_ERROR: "http_error",
    ERROR: "error",
} as const;

// Type for the hook that provides type safety for events
export const useSocket = <T extends SocketEvents = SocketEvents>({
    namespace,
    autoConnect = true,
}: {
    namespace: string;
    autoConnect?: boolean;
}) => {
    const uri = `${ENV.SOCKET_URL}/${namespace}`;
    const path = ENV.SOCKET_PATH;

    // Socket
    const socketRef = useRef<Socket<T & InternalSocketEvents> | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const triedRefreshOnceRef = useRef(false);

    // Auth
    const { auth, handleLogout } = useAuth();

    // Socket connection
    useEffect(() => {
        if (!auth?.accessToken) return;

        const socket: Socket<T & InternalSocketEvents> = io(uri, {
            transports: ["websocket"],
            path,
            auth: { token: auth?.accessToken },
            autoConnect: false,
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            setIsConnected(true);
            triedRefreshOnceRef.current = false;
        });
        socket.on("disconnect", () => setIsConnected(false));

        const handleHttpStatus = async (status?: number) => {
            if (status !== StatusCodes.UNAUTHORIZED) return;

            if (!triedRefreshOnceRef.current) {
                triedRefreshOnceRef.current = true;
                const refreshed = await handleTokenRefreshFlow();
                if (refreshed) {
                    const newToken = getCurrentAuthToken();
                    const s = socketRef.current;
                    if (s) {
                        s.auth = { token: newToken };
                        s.connect();
                    }
                    return;
                }
            }

            handleLogout();
        };

        socket.on("connect_error", async (err: Error & { status?: number; data?: { status?: number } }) => {
            const statusFromShape =
                typeof err.status === "number"
                    ? err.status
                    : typeof err.data?.status === "number"
                      ? err.data.status
                      : undefined;
            if (statusFromShape !== undefined) {
                await handleHttpStatus(statusFromShape);
                return;
            }
        });

        const onHttpError = (payload?: { status?: number } | null) => {
            const status = payload?.status;
            console.error("socket:http_error", payload);
            void handleHttpStatus(status);
        };
        type ErrorLike = { status?: number; code?: number } | null | undefined;

        const onAnyListener = (event: string, ...args: ErrorLike[]) => {
            if (event === SOCKET_INTERNAL_EVENTS.HTTP_ERROR) {
                const payload = args.length > 0 ? args[0] : undefined;
                if (payload && typeof payload === "object") {
                    const maybeStatus = payload.status;
                    onHttpError(typeof maybeStatus === "number" ? { status: maybeStatus } : null);
                    return;
                }
                onHttpError(null);
                return;
            }

            if (event === SOCKET_INTERNAL_EVENTS.ERROR) {
                const payload = args.length > 0 ? args[0] : undefined;
                if (payload && typeof payload === "object") {
                    console.error("socket:error", payload);
                    const maybeCode = payload.code;
                    void handleHttpStatus(typeof maybeCode === "number" ? maybeCode : undefined);
                    return;
                }
                console.error("socket:error (no-payload)");
                void handleHttpStatus(undefined);
            }
        };
        socket.onAny(onAnyListener);

        if (autoConnect) {
            socket.connect();
        }

        return () => {
            socket.disconnect();
        };
    }, [auth?.accessToken, autoConnect]);

    const useSocketCallback = <K extends keyof T & string>(event: K, callback: (data: T[K]) => void) => {
        useEffect(() => {
            if (socketRef.current) {
                const listener = (data: T[K]) => {
                    callback(data);
                };

                const socket = socketRef.current as unknown as {
                    on: (event: string, listener: (data: unknown) => void) => void;
                    off: (event: string, listener: (data: unknown) => void) => void;
                };

                socket.on(event, listener as (data: unknown) => void);
                return () => {
                    socket.off(event, listener as (data: unknown) => void);
                };
            }
        }, [event, callback]);
    };

    const emit = <K extends keyof T & string>(event: K, ...args: T[K] extends void ? [] : [T[K]] | [undefined]) => {
        if (socketRef.current && isConnected) {
            const socket = socketRef.current as unknown as {
                emit: (event: string, ...args: unknown[]) => void;
            };
            if (args.length === 0) {
                socket.emit(event);
                return;
            }
            socket.emit(event, args[0]);
        }
    };

    const connect = () => {
        const s = socketRef.current;
        if (s && !s.connected) s.connect();
    };

    return { socket: socketRef.current, isConnected, useSocketCallback, emit, connect };
};
